import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { useTheme } from "@/contexts/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Target, Eye, EyeOff, MapPin, Calendar, Camera, Video,
  Phone, Mail, User, DollarSign, X, CreditCard, TrendingUp
} from "lucide-react";
import PaymentMethodSelector from "@/components/PaymentMethodSelector";
import ProfileBlockerModal from "@/components/ProfileBlockerModal";
import CancellationPaymentBlockerModal from "@/components/CancellationPaymentBlockerModal";
import "@/styles/commandDeck.css";

const Leads = ({ onLogout }) => {
  const { isDark } = useTheme();

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showRevealModal, setShowRevealModal] = useState(false);
  const [revealLoading, setRevealLoading] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [leadPrice, setLeadPrice] = useState(50);
  const [showProfileBlocker, setShowProfileBlocker] = useState(false);
  const [userStatus, setUserStatus] = useState('');
  const [outstandingCancellationFees, setOutstandingCancellationFees] = useState(null);
  const [showCancellationBlocker, setShowCancellationBlocker] = useState(false);

  // New filter states
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'available', 'purchased'
  const [serviceFilter, setServiceFilter] = useState('all'); // 'all', 'photography', 'videography'
  const [expandedLeadId, setExpandedLeadId] = useState(null);

  const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');
  const userEmail = user.email;

  // Fetch user status from database on mount
  useEffect(() => {
    const fetchUserStatus = async () => {
      if (user.id) {
        try {
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/profile/${user.id}`);
          const dbStatus = (response.data.status || '').toUpperCase();
          console.log('📊 Leads - User status from DB:', dbStatus);
          setUserStatus(dbStatus);
        } catch (error) {
          console.error('Error fetching user status:', error);
        }
      }
      if (user.email) {
        try {
          const feesResponse = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/user/outstanding-cancellation-fees`,
            { params: { user_email: user.email } }
          );
          if (feesResponse.data.has_outstanding_fees) {
            setOutstandingCancellationFees(feesResponse.data);
            setShowCancellationBlocker(true);
          }
        } catch (err) {
          console.error('Failed to check cancellation fees:', err);
        }
      }
    };
    fetchUserStatus();
  }, [user.id]);

  useEffect(() => {
    if (userEmail) {
      fetchLeads();
      fetchSettings();
    }
  }, [userEmail]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/leads`,
        { params: { user_email: userEmail } }
      );

      if (response.data.success) {
        setLeads(response.data.leads || []);
      }
    } catch (error) {
      console.error('Fetch leads error:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/settings`
      );

      if (response.data.lead_reveal_price) {
        setLeadPrice(response.data.lead_reveal_price);
      }
    } catch (error) {
      console.error('Fetch settings error:', error);
    }
  };

  const handleRevealClick = (lead) => {
    // Block NEW and PENDING_APPROVAL users (using database status)
    if (userStatus === 'NEW' || userStatus === 'PENDING_APPROVAL') {
      console.log('🚫 Blocking reveal lead for status:', userStatus);
      setShowProfileBlocker(true);
      return;
    }

    setSelectedLead(lead);
    setShowRevealModal(true);
  };

  const handleRevealLead = async () => {
    if (!selectedPaymentMethod) {
      toast.error('Please select a payment method');
      return;
    }

    setRevealLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/leads/${selectedLead.id}/reveal`,
        {
          user_email: userEmail,
          payment_method_id: selectedPaymentMethod
        }
      );

      if (response.data.success) {
        toast.success('Lead purchased successfully!');
        setShowRevealModal(false);
        fetchLeads();
      }
    } catch (error) {
      console.error('Reveal lead error:', error);
      toast.error(error.response?.data?.detail || 'Failed to purchase lead');
    } finally {
      setRevealLoading(false);
    }
  };

  const getServiceIcon = (service) => {
    if (service === 'photography') return <Camera className="w-3.5 h-3.5" />;
    if (service === 'videography') return <Video className="w-3.5 h-3.5" />;
    return null;
  };

  const gstAmount = leadPrice * 0.10;
  const totalAmount = leadPrice + gstAmount;

  // Filter leads based on active tab and service filter
  const filteredLeads = leads.filter(lead => {
    // Tab filter
    if (activeTab === 'available' && (lead.user_has_purchased || !lead.can_purchase)) return false;
    if (activeTab === 'purchased' && !lead.user_has_purchased) return false;

    // Service filter
    if (serviceFilter !== 'all') {
      if (!lead.service_required || !lead.service_required.includes(serviceFilter)) return false;
    }

    return true;
  });

  // Stats counts
  const availableCount = leads.filter(l => l.can_purchase && !l.user_has_purchased).length;
  const purchasedCount = leads.filter(l => l.user_has_purchased).length;

  return (
    <div className="min-h-screen" style={{ background: '#0A0E1A' }}>
      <Sidebar onLogout={onLogout} />
      <Header />

      <div className="cd-root sidebar-content pt-16 lg:pt-20">
        {/* Animated bloom blobs */}
        <div className="cd-bloom-wrap">
          <div className="cd-bloom cd-bloom-1" />
          <div className="cd-bloom cd-bloom-2" />
        </div>

        <div className="cd-main">
          {/* Header */}
          <div className="cd-col-mobile" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, marginBottom: 22 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span className="cd-display" style={{ fontWeight: 700, fontSize: 11, letterSpacing: 2, color: '#C98AD4' }}>DIRECT ENQUIRIES</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: 700, fontSize: 11.5, color: '#67C6B4', background: 'rgba(103,198,180,.16)', padding: '4px 11px', borderRadius: 999 }}>
                  {availableCount} leads available
                </span>
              </div>
              <div className="cd-display" style={{ fontWeight: 800, fontSize: 32, letterSpacing: '-1px', color: '#fff', marginTop: 8 }}>Leads near you</div>
            </div>
          </div>

          {/* Stats */}
          <div className="cd-col-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 18 }}>
            <div className="cd-stat" style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 16, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ width: 42, height: 42, borderRadius: 11, background: 'rgba(103,198,180,.16)', color: '#67C6B4', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                <Target className="w-5 h-5" />
              </span>
              <div>
                <div className="cd-display" style={{ fontWeight: 800, fontSize: 26, color: '#fff', lineHeight: 1 }}>{availableCount}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,.55)', marginTop: 3 }}>Available</div>
              </div>
            </div>
            <div className="cd-stat" style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 16, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ width: 42, height: 42, borderRadius: 11, background: 'rgba(40,192,208,.16)', color: '#28C0D0', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                <Eye className="w-5 h-5" />
              </span>
              <div>
                <div className="cd-display" style={{ fontWeight: 800, fontSize: 26, color: '#fff', lineHeight: 1 }}>{purchasedCount}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,.55)', marginTop: 3 }}>Purchased</div>
              </div>
            </div>
            <div className="cd-stat" style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 16, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ width: 42, height: 42, borderRadius: 11, background: 'rgba(120,40,124,.24)', color: '#C98AD4', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                <DollarSign className="w-5 h-5" />
              </span>
              <div>
                <div className="cd-display" style={{ fontWeight: 800, fontSize: 26, color: '#fff', lineHeight: 1 }}>${leadPrice}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,.55)', marginTop: 3 }}>Per lead · +${gstAmount.toFixed(2)} GST</div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="cd-col-mobile" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
            <div className="cd-tabs">
              <button type="button" onClick={() => setActiveTab('all')} className={`cd-tab ${activeTab === 'all' ? 'cd-tab-active' : ''}`}>All leads</button>
              <button type="button" onClick={() => setActiveTab('available')} className={`cd-tab ${activeTab === 'available' ? 'cd-tab-active' : ''}`}>Available</button>
              <button type="button" onClick={() => setActiveTab('purchased')} className={`cd-tab ${activeTab === 'purchased' ? 'cd-tab-active' : ''}`}>Purchased</button>
            </div>
            <div className="cd-tabs">
              <button type="button" onClick={() => setServiceFilter('all')} className={`cd-tab ${serviceFilter === 'all' ? 'cd-tab-active' : ''}`}>All services</button>
              <button type="button" onClick={() => setServiceFilter('photography')} className={`cd-tab ${serviceFilter === 'photography' ? 'cd-tab-active' : ''}`}>
                <Camera className="w-3.5 h-3.5" />Photography
              </button>
              <button type="button" onClick={() => setServiceFilter('videography')} className={`cd-tab ${serviceFilter === 'videography' ? 'cd-tab-active' : ''}`}>
                <Video className="w-3.5 h-3.5" />Videography
              </button>
            </div>
          </div>

          {/* States */}
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="cd-skel">
                  <span style={{ width: 90, height: 64, borderRadius: 12, background: 'rgba(255,255,255,.08)', flex: '0 0 auto' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ width: 120, height: 11, borderRadius: 6, background: 'rgba(255,255,255,.08)', marginBottom: 10 }} />
                    <div style={{ width: '55%', height: 16, borderRadius: 6, background: 'rgba(255,255,255,.1)', marginBottom: 10 }} />
                    <div style={{ width: '38%', height: 10, borderRadius: 6, background: 'rgba(255,255,255,.07)' }} />
                  </div>
                  <div style={{ width: 80, height: 36, borderRadius: 10, background: 'rgba(255,255,255,.08)', flex: '0 0 auto' }} />
                </div>
              ))}
            </div>
          ) : filteredLeads.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '60px 20px', background: 'rgba(255,255,255,.025)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 18 }}>
              <span style={{ width: 64, height: 64, borderRadius: 18, background: 'rgba(40,192,208,.12)', color: '#28C0D0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
                <Target className="w-[30px] h-[30px]" />
              </span>
              <div className="cd-display" style={{ fontWeight: 700, fontSize: 19, color: '#fff' }}>
                {activeTab === 'purchased' ? 'No purchased leads yet' : 'No leads available'}
              </div>
              <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,.55)', marginTop: 7, maxWidth: 340, lineHeight: 1.55 }}>
                {activeTab === 'purchased'
                  ? 'When you purchase leads, they will appear here.'
                  : 'New client enquiries near you will show up here as they come in.'}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {filteredLeads.map((lead) => {
                const isPurchased = lead.user_has_purchased;
                const isFull = !isPurchased && !lead.can_purchase;
                const isExpanded = expandedLeadId === lead.id;
                const initials = `${(lead.first_name || '')[0] || ''}${(lead.last_name || '')[0] || ''}`.toUpperCase();

                // Card chrome per state
                let cardStyle = { background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.09)' };
                if (isPurchased) cardStyle = { background: 'rgba(40,192,208,.05)', border: '1px solid rgba(40,192,208,.22)' };
                else if (isFull) cardStyle = { background: 'rgba(255,255,255,.02)', border: '1px solid rgba(255,255,255,.07)', opacity: .72 };

                // Right panel chrome
                let panelBg = 'rgba(120,40,124,.08)';
                if (isPurchased) panelBg = 'rgba(40,192,208,.06)';
                else if (isFull) panelBg = 'rgba(255,255,255,.02)';

                return (
                  <div
                    key={lead.id}
                    onClick={() => setExpandedLeadId(isExpanded ? null : lead.id)}
                    className={`cd-col-mobile ${!isPurchased && !isFull ? 'cd-card' : ''}`}
                    style={{ display: 'flex', borderRadius: 16, overflow: 'hidden', cursor: 'pointer', ...cardStyle }}
                  >
                    {/* Left — lead info */}
                    <div style={{ flex: 1, minWidth: 0, padding: '20px 22px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', marginBottom: 10 }}>
                        <span className="cd-mono" style={{ fontSize: 11.5, color: 'rgba(255,255,255,.4)' }}>{lead.lead_number}</span>
                        {isPurchased ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: '#28C0D0', background: 'rgba(40,192,208,.16)', padding: '3px 9px', borderRadius: 999 }}>
                            <Eye className="w-[11px] h-[11px]" />Purchased
                          </span>
                        ) : isFull ? (
                          <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,.6)', background: 'rgba(255,255,255,.08)', padding: '3px 9px', borderRadius: 999 }}>Full</span>
                        ) : (
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#67C6B4', background: 'rgba(103,198,180,.16)', padding: '3px 9px', borderRadius: 999 }}>Available</span>
                        )}
                        <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,.55)' }}>
                          {lead.reveals_count} of {lead.max_reveals} reveals
                        </span>
                        {(lead.service_required || []).map((s) => (
                          <span key={s} className="cd-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, textTransform: 'capitalize' }}>
                            {getServiceIcon(s)}{s}
                          </span>
                        ))}
                      </div>

                      {/* Name + avatar */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {isPurchased ? (
                          <span className="cd-display" style={{ width: 38, height: 38, borderRadius: 999, background: 'linear-gradient(135deg,#572B7C,#28C0D0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 12, flex: '0 0 auto' }}>
                            {initials || <User className="w-[18px] h-[18px]" />}
                          </span>
                        ) : (
                          <span style={{ width: 38, height: 38, borderRadius: 999, background: 'rgba(255,255,255,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,.5)', flex: '0 0 auto' }}>
                            <User className="w-[18px] h-[18px]" />
                          </span>
                        )}
                        <div className="cd-display" style={{ fontWeight: 700, fontSize: 18, color: isFull ? 'rgba(255,255,255,.7)' : '#fff', filter: isPurchased ? 'none' : 'blur(.3px)', letterSpacing: isPurchased ? 'normal' : 1 }}>
                          {isPurchased
                            ? `${lead.first_name} ${lead.last_name}`
                            : `${lead.first_name_masked} ${lead.last_name_masked}`}
                        </div>
                      </div>

                      {/* Info row */}
                      <div style={{ display: 'flex', gap: 24, marginTop: 14, flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,.45)' }}>Date required</div>
                          <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff', marginTop: 2 }}>
                            {new Date(lead.date_required).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,.45)' }}>Location</div>
                          <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff', marginTop: 2 }}>
                            {lead.location}
                            {lead.distance_km && (
                              <span style={{ color: 'rgba(255,255,255,.5)', fontWeight: 500 }}> ({lead.distance_km}km)</span>
                            )}
                          </div>
                        </div>
                        {lead.budget && (
                          <div>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,.45)' }}>Budget</div>
                            <div style={{ fontSize: 13.5, fontWeight: 600, color: '#67C6B4', marginTop: 2 }}>{lead.budget}</div>
                          </div>
                        )}
                      </div>

                      {/* Brief */}
                      {lead.brief && (
                        <div style={{ marginTop: 14 }}>
                          <div
                            style={{
                              fontSize: 13, color: 'rgba(255,255,255,.6)', lineHeight: 1.55, whiteSpace: 'pre-line',
                              ...(isExpanded ? {} : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' })
                            }}
                          >
                            {lead.brief}
                          </div>
                          {lead.brief.length > 120 && (
                            <span style={{ color: '#28C0D0', cursor: 'pointer', fontWeight: 600, fontSize: 12.5, marginTop: 4, display: 'inline-block' }}>
                              {isExpanded ? 'Show less' : 'Read more'}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Right — contact + action */}
                    <div className="cd-w-full-m" style={{ flex: '0 0 240px', background: panelBg, borderLeft: '1px solid rgba(255,255,255,.08)', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 22, gap: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9, color: isPurchased ? '#fff' : 'rgba(255,255,255,.5)', fontSize: 13 }}>
                        <Phone className="w-[15px] h-[15px]" style={{ color: isPurchased ? '#28C0D0' : 'currentColor', flex: '0 0 auto' }} />
                        <span className="cd-mono" style={{ filter: isPurchased ? 'none' : 'blur(.3px)' }}>
                          {isPurchased ? lead.phone_number : lead.phone_masked}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9, color: isPurchased ? '#fff' : 'rgba(255,255,255,.5)', fontSize: 13, minWidth: 0 }}>
                        <Mail className="w-[15px] h-[15px]" style={{ color: isPurchased ? '#28C0D0' : 'currentColor', flex: '0 0 auto' }} />
                        <span style={{ filter: isPurchased ? 'none' : 'blur(.3px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {isPurchased ? lead.email : lead.email_masked}
                        </span>
                      </div>

                      {/* Action button */}
                      {isPurchased ? (
                        <button
                          type="button"
                          disabled
                          style={{ marginTop: 6, padding: 12, borderRadius: 12, background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.16)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'default', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                        >
                          <Eye className="w-4 h-4" />Lead Revealed
                        </button>
                      ) : isFull ? (
                        <button
                          type="button"
                          disabled
                          style={{ marginTop: 6, width: '100%', padding: 13, borderRadius: 12, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)', color: 'rgba(255,255,255,.45)', fontWeight: 600, fontSize: 13, cursor: 'not-allowed', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                        >
                          <EyeOff className="w-4 h-4" />Lead Full
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleRevealClick(lead); }}
                          style={{ marginTop: 6, padding: 13, borderRadius: 12, background: '#78287C', border: 'none', color: '#fff', fontWeight: 700, fontSize: 13.5, cursor: 'pointer', boxShadow: '0 12px 28px -12px rgba(120,40,124,.8)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                        >
                          <Eye className="w-4 h-4" />Reveal lead — ${leadPrice}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Reveal Modal */}
      {showRevealModal && selectedLead && (
        <div className="cd-modal-overlay">
          <div className="cd-modal-card" style={{ maxWidth: 460 }}>
            {/* Header */}
            <div className="cd-modal-header">
              <button type="button" onClick={() => setShowRevealModal(false)} className="cd-modal-close">
                <X className="w-[17px] h-[17px]" />
              </button>
              <div className="cd-display" style={{ fontWeight: 700, fontSize: 11, letterSpacing: 2, color: '#E9D5F0' }}>
                {selectedLead.lead_number}
              </div>
              <div className="cd-display" style={{ fontWeight: 800, fontSize: 22, color: '#fff', letterSpacing: '-.5px', marginTop: 6 }}>
                Reveal this lead?
              </div>
              <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,.85)', marginTop: 5, lineHeight: 1.5 }}>
                You'll get the client's name, phone and email — and can pitch directly. {selectedLead.reveals_count} of {selectedLead.max_reveals} reveals left on this lead.
              </div>
            </div>

            <div style={{ padding: '22px 26px' }}>
              {/* Lead Summary */}
              <div style={{ border: '1px solid rgba(255,255,255,.1)', borderRadius: 14, padding: 16, marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: .6, color: 'rgba(255,255,255,.4)', marginBottom: 11 }}>LEAD SUMMARY</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,.55)' }}>Services</span>
                    <span style={{ fontSize: 13, color: '#fff', fontWeight: 600, textTransform: 'capitalize', textAlign: 'right' }}>{selectedLead.service_required.join(', ')}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,.55)' }}>Location</span>
                    <span style={{ fontSize: 13, color: '#fff', fontWeight: 600, textAlign: 'right' }}>{selectedLead.location}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,.55)' }}>Date required</span>
                    <span style={{ fontSize: 13, color: '#fff', fontWeight: 600, textAlign: 'right' }}>
                      {new Date(selectedLead.date_required).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Important Terms */}
              <div style={{ background: 'rgba(252,211,77,.08)', border: '1px solid rgba(252,211,77,.28)', borderRadius: 14, padding: 16, marginBottom: 18 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#FCD34D', marginBottom: 9 }}>Important terms</div>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {[
                    'Lead from verified public contact form',
                    'No job guarantee — you must pitch to win the client',
                    'Limited to first 6 photographers/videographers',
                    'Non-refundable purchase'
                  ].map((t, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: 'rgba(252,211,77,.85)', lineHeight: 1.5 }}>
                      <span style={{ color: '#FCD34D', marginTop: 1 }}>•</span>
                      <span style={{ fontWeight: i === 3 ? 700 : 400 }}>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Price Breakdown */}
              <div style={{ border: '1px solid rgba(255,255,255,.1)', borderRadius: 13, padding: '6px 16px', marginBottom: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,.65)' }}>Reveal fee</span>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,.85)', fontWeight: 500 }}>${leadPrice.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,.65)' }}>GST (10%)</span>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,.85)', fontWeight: 500 }}>${gstAmount.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0' }}>
                  <span style={{ fontSize: 13.5, color: '#fff', fontWeight: 700 }}>Total</span>
                  <span className="cd-display" style={{ fontWeight: 800, fontSize: 20, color: '#fff' }}>${totalAmount.toFixed(2)}</span>
                </div>
              </div>

              {/* Payment Method */}
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: .6, color: 'rgba(255,255,255,.4)', marginBottom: 9, display: 'flex', alignItems: 'center', gap: 8 }}>
                <CreditCard className="w-4 h-4" style={{ color: '#28C0D0' }} />
                PAYMENT METHOD
              </div>
              <div style={{ marginBottom: 18 }}>
                <PaymentMethodSelector onPaymentMethodSelected={(id) => setSelectedPaymentMethod(id)} />
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setShowRevealModal(false)}
                  style={{ flex: 1, padding: 13, borderRadius: 12, background: 'transparent', border: '1px solid rgba(255,255,255,.2)', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRevealLead}
                  disabled={!selectedPaymentMethod || revealLoading}
                  style={{ flex: 1.6, padding: 13, borderRadius: 12, background: '#78287C', border: 'none', color: '#fff', fontWeight: 700, fontSize: 14, cursor: (!selectedPaymentMethod || revealLoading) ? 'not-allowed' : 'pointer', boxShadow: '0 12px 28px -12px rgba(120,40,124,.8)', opacity: (!selectedPaymentMethod || revealLoading) ? .5 : 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                >
                  {revealLoading ? (
                    <>
                      <div className="animate-spin" style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,.3)', borderTopColor: '#fff', borderRadius: 999 }} />
                      Processing...
                    </>
                  ) : (
                    <>Pay ${totalAmount.toFixed(2)}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Blocker Modal */}
      <ProfileBlockerModal
        isOpen={showProfileBlocker}
        onClose={() => setShowProfileBlocker(false)}
        userStatus={userStatus}
        userName={user.full_name || user.first_name || 'User'}
        userId={user.id}
      />

      {/* Cancellation Payment Blocker Modal */}
      <CancellationPaymentBlockerModal
        isOpen={showCancellationBlocker}
        outstandingFees={outstandingCancellationFees}
      />
    </div>
  );
};

export default Leads;
